/**
 * Utilitários para extrair dados estruturados da planilha Univer.
 * Combina 3 abordagens: metadados, busca por texto e fallback por posição.
 */

/**
 * Busca células que contêm um texto específico (case-insensitive).
 * Retorna array de { row, col, value, displayValue }
 *
 * @param {Object} fWorksheet - Worksheet do Univer (via Facade API)
 * @param {string} searchText - Texto para buscar
 * @param {Object} options - Opções de busca
 * @param {boolean} options.exactMatch - Se true, busca correspondência exata
 * @param {number} options.maxRows - Máximo de linhas para buscar (default: 200)
 * @param {number} options.maxCols - Máximo de colunas para buscar (default: 30)
 * @returns {Array} Array de células encontradas
 */
export const findCellsByText = (fWorksheet, searchText, options = {}) => {
  const { exactMatch = false, maxRows = 200, maxCols = 30 } = options; // <-- AJUSTADO
  const results = [];

  const searchLower = searchText.toLowerCase();

  // Pega o range de busca (limitado pelo tamanho real da sheet)
  const rowCount = fWorksheet.getMaxRows();
  const colCount = fWorksheet.getMaxColumns();

  const safeMaxRows = Math.min(maxRows, rowCount);
  const safeMaxCols = Math.min(maxCols, colCount);

  // Pega o range de busca
  const range = fWorksheet.getRange(0, 0, safeMaxRows, safeMaxCols);
  const values = range.getValues();
  const displayValues = range.getDisplayValues();

  for (let row = 0; row < values.length; row++) {
    for (let col = 0; col < values[row].length; col++) {
      const value = values[row][col];
      const displayValue = displayValues[row][col];

      if (value === null || value === undefined) continue;

      const valueStr = String(value).toLowerCase();
      const displayStr = String(displayValue).toLowerCase();

      const matches = exactMatch
        ? valueStr === searchLower || displayStr === searchLower
        : valueStr.includes(searchLower) || displayStr.includes(searchLower);

      if (matches) {
        results.push({
          row,
          col,
          value,
          displayValue,
          cellRef: `${String.fromCharCode(65 + col)}${row + 1}`,
        });
      }
    }
  }

  return results;
};

/**
 * Busca uma célula por texto e retorna o valor da célula ao lado (direita).
 * Útil para encontrar "TOTAL:" e pegar o valor da célula ao lado.
 *
 * @param {Object} fWorksheet - Worksheet do Univer
 * @param {string} labelText - Texto do label (ex: "TOTAL FATURAMENTO:")
 * @param {number} offsetCol - Offset da coluna do valor (default: 1 = célula à direita)
 * @returns {any} Valor da célula encontrada ou null
 */
export const findValueByLabel = (fWorksheet, labelText, offsetCol = 1) => {
  const found = findCellsByText(fWorksheet, labelText, { exactMatch: false });

  if (found.length === 0) return null;

  // Pega a primeira ocorrência
  const { row, col } = found[0];
  const valueCell = fWorksheet.getRange(row, col + offsetCol);

  return valueCell.getValue();
};

/**
 * Extrai dados de uma tabela usando metadados das células.
 * Só extrai os campos presentes em `requestedFields` — mesmo que a tabela
 * tenha outras colunas com metadata, elas são ignoradas se não foram pedidas.
 *
 * @param {Object} fWorksheet - Worksheet do Univer
 * @param {Object} tableDef - Definição da tabela (com metadata nas colunas)
 * @param {Array<string>} requestedFields - Lista de `key`s que você quer extrair
 * @param {number} startRow - Linha inicial da tabela
 * @param {number} startCol - Coluna inicial da tabela
 * @param {number} dataLength - Número de linhas de dados
 * @returns {Array} Array de objetos, uma chave por campo pedido
 */
export const extractTableByMetadata = (
  fWorksheet,
  tableDef,
  requestedFields,
  startRow,
  startCol,
  dataLength,
) => {
  const items = [];

  // Só considera colunas que (a) têm metadata na definição E (b) foram pedidas
  const columnsToExtract = tableDef.columns
    .map((col, index) => ({ ...col, colIndex: index }))
    .filter((col) => col.metadata && requestedFields?.includes(col.key));

  if (columnsToExtract.length === 0) return [];

  for (let i = 0; i < dataLength; i++) {
    const absoluteRow = startRow + i;
    const item = {};

    columnsToExtract.forEach((col) => {
      const absoluteCol = startCol + col.colIndex;
      const value = fWorksheet.getRange(absoluteRow, absoluteCol).getValue();
      item[col.key] = value; // <-- usa col.key, o mesmo nome que você pediu
    });

    if (
      Object.values(item).some((v) => v !== null && v !== undefined && v !== "")
    ) {
      items.push(item);
    }
  }

  return items;
};

/**
 * Extrai dados de uma tabela usando posições fixas (fallback).
 *
 * @param {Object} fWorksheet - Worksheet do Univer
 * @param {Array} columnKeys - Array de chaves na ordem das colunas
 * @param {number} startRow - Linha inicial dos dados
 * @param {number} startCol - Coluna inicial
 * @param {number} dataLength - Número de linhas
 * @returns {Array} Array de objetos extraídos
 */
export const extractTableByPosition = (
  fWorksheet,
  columnKeys,
  startRow,
  startCol,
  dataLength,
) => {
  const items = [];

  for (let i = 0; i < dataLength; i++) {
    const absoluteRow = startRow + i;
    const item = {};

    columnKeys.forEach((key, colIndex) => {
      if (!key) return; // Pula colunas sem chave

      const absoluteCol = startCol + colIndex;
      const cell = fWorksheet.getRange(absoluteRow, absoluteCol);
      const value = cell.getValue();

      item[key] = value;
    });

    // Só adiciona se tem pelo menos um campo preenchido
    if (
      Object.values(item).some((v) => v !== null && v !== undefined && v !== "")
    ) {
      items.push(item);
    }
  }

  return items;
};

/**
 * Extrai totais da planilha buscando por labels específicos.
 *
 * @param {Object} fWorksheet - Worksheet do Univer
 * @returns {Object} Objeto com totais encontrados
 */
export const extractTotals = (fWorksheet) => {
  const totals = {};

  // Busca "TOTAL GERAL:"
  const totalGeral = findValueByLabel(fWorksheet, "TOTAL GERAL:");
  if (totalGeral !== null) {
    totals.totalGeral = totalGeral;
  }

  // Busca "TOTAL:" (da tabela de equipamentos)
  const totalTipos = findValueByLabel(fWorksheet, "TOTAL:");
  if (totalTipos !== null) {
    totals.totalTipos = totalTipos;
  }

  // Busca "TOTAL FATURAMENTO:" (tabela manual ou automática)
  const totalFaturamento = findValueByLabel(fWorksheet, "TOTAL FATURAMENTO:");
  if (totalFaturamento !== null) {
    totals.totalFaturamento = totalFaturamento;
  }

  return totals;
};

/**
 * Converte serial date do Excel para JS Date.
 *
 * @param {number} serial - Serial date do Excel
 * @returns {Date|null} Data JS ou null se inválido
 */
export const excelSerialToDate = (serial) => {
  if (!serial || typeof serial !== "number") return null;

  // Excel epoch: 30 dez 1899 (inclui bug histórico de 1900)
  const excelEpoch = new Date(Date.UTC(1899, 11, 30));
  const date = new Date(excelEpoch.getTime() + serial * 24 * 60 * 60 * 1000);

  return isNaN(date.getTime()) ? null : date;
};

/**
 * Formata data para string YYYY-MM-DD (formato do backend).
 *
 * @param {Date} date - Data JS
 * @returns {string|null} Data formatada ou null
 */
export const formatDateForBackend = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return null;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/**
 * Descobre quantas linhas de dados reais uma tabela tem a partir de startRow,
 * parando na primeira linha totalmente vazia OU que contenha o label de total
 * da tabela (ex: "TOTAL GERAL:") — o que vier primeiro.
 * Isso evita que a extração "vaze" para a próxima tabela empilhada nas mesmas colunas.
 */
export const findTableDataLength = (
  fWorksheet,
  tableDef,
  startRow,
  startCol,
  numColumns,
  maxScan,
) => {
  const totalLabels = (tableDef?.totals || [])
    .map((t) => t.label)
    .filter(Boolean)
    .map((label) => label.toLowerCase());

  for (let i = 0; i < maxScan; i++) {
    const absoluteRow = startRow + i;
    let hasValue = false;
    let isBoundaryRow = false;

    for (let c = 0; c < numColumns; c++) {
      const value = fWorksheet.getRange(absoluteRow, startCol + c).getValue();

      if (value !== null && value !== undefined && value !== "") {
        hasValue = true;
        if (totalLabels.includes(String(value).toLowerCase())) {
          isBoundaryRow = true;
        }
      }
    }

    if (!hasValue || isBoundaryRow) {
      return i; // linha em branco ou linha de total = fim dos dados
    }
  }

  return maxScan;
};
